import React from 'react';
import { Card } from 'react-bootstrap';
import './bloodwork-anemia-status.css';
import { BloodworkAnalysis } from '../../models/BloodworkAnalysis';
import { PanelMarker } from '../../models/PanelMarker';
import { Section } from '../../models/Section';
import {
  markerStatus,
  sectionMarkerCounts,
  pillStyle,
  STATUS_LABEL,
  Status,
} from './results-overview-panel';
import {
  resolveOneCarbonPatterns,
  resolveOpenQuestions,
  NutrientMarkerStates,
  NutrientState,
} from '../../models/OneCarbonAnalysis';
import {
  NUTRIENT_PATTERN_INSIGHTS,
  NUTRIENT_OPEN_QUESTIONS,
  NutrientLogicMarker,
  NutrientOpenQuestion,
  NutrientPatternInsight,
} from '../../models/nutrient-pattern-insights.generated';
import { nutrientPatternsVisible } from '../../utils/nutrientPatternsVisibility';

const arrow_up = require('../../assets/imgs/long-arrow-up-8x13.png');
const arrow_down = require('../../assets/imgs/long-arrow-down-8x12.png');

/*
 * NO HERO GAUGE — this is a deliberate departure from Glucose, Thyroid, Adrenal
 * and Hydration, not an omission (decided 2026-08-21).
 *
 * Those sections infer a hidden condition from proxies, so "Highly Likely (95%)"
 * means something. Here the marker IS the finding: "Vitamin D Dysfunction:
 * Highly Likely" is a worse sentence than "Vitamin D 22 ng/mL, Priority". The
 * body of the panel therefore uses the Results Overview severity vocabulary
 * (Optimal / Monitor / Support / Priority) rather than likelihood words.
 *
 * A composite "nutrient burden" dial was considered and rejected: it carries the
 * same panel-composition confound already documented for Results Overview, where
 * 1-of-3 markers low and 6-of-19 low point the same needle. The status tally
 * plus the Open Questions block says the same thing without a number that cannot
 * be compared between clients.
 */

const PANEL_TITLE = 'Vitamin & Mineral Insights';

// Ordered worst-first: the focus list and the tally both read top-down.
const STATUS_ORDER: Status[] = ['red', 'orange', 'yellow', 'green'];

/*
 * Status color is never defined here. Both the tally and the focus-group headers
 * render Results Overview's ACTUAL pill (its exported `pillStyle`), so the two
 * panels cannot drift apart the way the MOO colors did when they lived in three
 * separate places.
 */

/*
 * The focus list is NOT capped. A "+5 more" line raises the one question the
 * panel exists to answer ("which five?") and forces a jump to the Results table
 * to find out. Only non-optimal markers reach this list, so it stays short on
 * its own; a panel long enough to feel heavy is a panel with genuinely that many
 * things to act on.
 */

const arrowStyle: React.CSSProperties = {
  height: '11px',
  margin: '0 3px',
  verticalAlign: 'middle',
  position: 'relative',
  top: '-0.5px',
};
const sectionLabelStyle: React.CSSProperties = {
  textAlign: 'left',
  fontWeight: 600,
  color: 'black',
  marginTop: '10px',
};
/*
 * Two even columns, filled DOWN THEN OVER (`grid-auto-flow: column`), so the eye
 * meets Priority and Support first in the left column and Monitor/Optimal
 * second. Row-major flow would have put Support top-right and Monitor
 * bottom-left, which reads the second-most-urgent status last.
 *
 * Flex-wrap was worse still: it laid four out as 3 + 1 and orphaned "Optimal"
 * on a line of its own.
 *
 * The ROW COUNT follows the pill count rather than being a fixed two: two pills
 * on two rows left half the panel's width empty beside a stack of two, which is
 * the widest thing in the panel wasting the most space. One row from two pills
 * puts them side by side; three or four still take two rows, where the
 * down-then-over reading order is what matters.
 */
const tallyRowStyle = (statuses: number): React.CSSProperties => ({
  display: 'grid',
  gridAutoFlow: 'column',
  gridTemplateRows: `repeat(${statuses > 2 ? 2 : 1}, auto)`,
  gridTemplateColumns: statuses > 1 ? '1fr 1fr' : '1fr',
  gap: '5px 6px',
  marginBottom: '2px',
  justifyItems: 'center',
});
const focusRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  gap: '6px',
  color: 'black',
  lineHeight: 1.35,
};
// Matches the Possible Root Causes boxes in the anemia panel
// (bloodwork-anemia-status.css) so the two panels read as one system.
const boxStyle: React.CSSProperties = {
  border: '3px solid #c1b7b7',
  borderRadius: '10px',
  padding: '8px 10px',
  marginTop: '8px',
  textAlign: 'left',
};
const questionStyle: React.CSSProperties = {
  textAlign: 'left',
  color: 'black',
  lineHeight: 1.35,
  marginTop: '5px',
};
const focusGroupStyle: React.CSSProperties = { marginTop: '5px' };
/*
 * The focus groups split across two columns on the same down-then-over rule as
 * the tally: the urgent half (Priority, Support) fills the left column, the rest
 * fills the right. A single column left a wide empty gutter, since marker names
 * use nowhere near the panel's 300px.
 */
const FOCUS_COLUMNS: Status[][] = [
  ['red', 'orange'],
  ['yellow', 'green'],
];
const focusColumnsStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  columnGap: '8px',
  alignItems: 'start',
};
// Hanging indent: a wrapped bullet line stays clear of the glyph rather than
// running back under it.
const bulletListStyle: React.CSSProperties = {
  paddingLeft: 0,
  marginLeft: 0,
  marginTop: '3px',
  marginBottom: 0,
  listStyle: 'none',
};
const bulletItemStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  gap: '4px',
  color: 'black',
};
const bulletGlyphStyle: React.CSSProperties = { flex: '0 0 auto' };
// Matches thyroid-regulation-insights' markerStyle.
const markerRowStyle: React.CSSProperties = {
  marginTop: '4px',
  color: 'black',
  textAlign: 'right',
};
const arrowSlotStyle: React.CSSProperties = {
  display: 'inline-block',
  width: '13px',
  flex: '0 0 auto',
  textAlign: 'center',
};

type Props = { bloodworkAnalysis: BloodworkAnalysis };

type FocusRow = { label: string; status: Status; direction: 'up' | 'down' | null; magnitude: number };

export class NutrientInsights extends React.Component<Props, {}> {
  // ── Marker classification (mirrors GlucoseRegulationInsights / Thyroid) ─────
  static isHigh = (m: any) => !!(m?.isGreaterThanLab?.() || m?.isGreaterThanOptimal?.());
  static isLow = (m: any) =>
    !!(m?.isLessThanLab?.() || m?.isStricklyLessThanOptimal?.() || m?.isLessThanOptimal?.());
  static hasValue = (m: any) => !!m?.hasValue?.();

  static state1(m: any): NutrientState {
    if (!NutrientInsights.hasValue(m)) return 'absent';
    if (NutrientInsights.isHigh(m)) return 'high';
    if (NutrientInsights.isLow(m)) return 'low';
    return 'optimal';
  }

  /**
   * MCV, RDW and eGFR are read from other sections here. The CBC is ordered
   * for practically everyone, so they are what let this family say something
   * useful on the most common panel of all: a CBC plus B12.
   */
  static buildStates(ba: BloodworkAnalysis): NutrientMarkerStates {
    const s = NutrientInsights.state1;
    return {
      vitaminB12: s(ba.vitaminB12),
      vitaminB6: s(ba.vitaminB6),
      folateSerum: s(ba.folateSerum),
      folateRBC: s(ba.folateRBC),
      homocysteine: s(ba.homocysteine),
      mcv: s(ba.mcv),
      rdw: s(ba.rdw),
      eGFR: s(ba.eGFR),
    };
  }

  /**
   * Every one-carbon pattern this test satisfies, ignoring the visibility gate.
   * Kept separate from `patternIds` so the resolution rules and the pattern text
   * stay exercisable while the boxes are hidden.
   */
  static resolvedPatternIds(ba: BloodworkAnalysis): number[] {
    return resolveOneCarbonPatterns(NutrientInsights.buildStates(ba)).filter(
      (id) => !!NUTRIENT_PATTERN_INSIGHTS[id],
    );
  }

  /**
   * The patterns this panel may SHOW. Empty while the boxes are gated off (see
   * nutrientPatternsVisible), which is the one place that decision is made: the
   * render, `shouldRender` and the PDF height estimate all read this, so screen
   * and PDF cannot disagree about whether a pattern exists.
   */
  static patternIds(ba: BloodworkAnalysis): number[] {
    return nutrientPatternsVisible() ? NutrientInsights.resolvedPatternIds(ba) : [];
  }

  /**
   * Every open question this test raises, ignoring the visibility gate. Kept
   * separate from `questionIds` for the reason `resolvedPatternIds` is kept
   * apart from `patternIds`: the resolution rules and the question text stay
   * exercisable while the list is hidden.
   */
  static resolvedQuestionIds(ba: BloodworkAnalysis): number[] {
    return resolveOpenQuestions(NutrientInsights.buildStates(ba)).filter(
      (id) => !!NUTRIENT_OPEN_QUESTIONS[id],
    );
  }

  /**
   * The questions this panel may SHOW. Empty while the gate is shut: the
   * questions are authored on the same unreviewed "Nutrient Patterns" tab as
   * the patterns, so they are held with them rather than on a switch of their
   * own. Read by the render, `shouldRender` and the PDF height estimate alike.
   */
  static questionIds(ba: BloodworkAnalysis): number[] {
    return nutrientPatternsVisible() ? NutrientInsights.resolvedQuestionIds(ba) : [];
  }

  /** Markers below optimal, worst first. Optimal ones never reach the list. */
  static focusRows(markers: PanelMarker[]): FocusRow[] {
    const rows: FocusRow[] = [];
    for (const pm of markers) {
      const status = markerStatus(pm, Section.NUTRIENTS);
      if (!status || status === 'green') continue;
      rows.push({
        label: pm.field?.shortLabel || pm.field?.label || pm.attribute,
        status,
        direction: NutrientInsights.isHigh(pm) ? 'up' : NutrientInsights.isLow(pm) ? 'down' : null,
        // percentFromMean is already normalized (66.67 = optimal bound, 100 =
        // lab bound, 133 = alarm), so its magnitude ranks within a color band
        // without any new math.
        magnitude: Math.abs(pm.percentFromMean ?? 0),
      });
    }
    return rows.sort(
      (a, b) =>
        STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status) ||
        b.magnitude - a.magnitude,
    );
  }

  /**
   * Render whenever the section has anything to say. A panel with markers but no
   * findings still earns its place: the tally is the "nothing needs attention
   * here" statement, and the questions say what could not be checked.
   *
   * The one exception is a section whose whole content is a SINGLE optimal
   * marker. An optimal marker never reaches the Focus list, so with no pattern
   * and no question the panel is a card wrapped around one "1 Optimal" pill —
   * a heading and a border to say less than the bar graph beside it already
   * does. Hiding it lets the Results panel left-align (see the fullWidth path
   * in bloodwork-test-analysis-section.tsx) instead of sitting beside a
   * near-empty column. Two or more optimal markers still render: "6 Optimal"
   * is a statement about the panel, one is a formality.
   *
   * While nutrientPatternsVisible() is shut, `hasFindings` can never be true,
   * so that exception applies to every single-optimal-marker panel rather than
   * only the ones raising no question. A CBC plus an in-range B12 — the
   * commonest sparse panel there is — shows no Insights panel at all.
   */
  static shouldRender(ba: BloodworkAnalysis): boolean {
    const markers = ba.getMineralPanelMarkers?.() ?? [];
    const { counts, total } = sectionMarkerCounts(markers, Section.NUTRIENTS);
    const hasFindings =
      NutrientInsights.patternIds(ba).length > 0 ||
      NutrientInsights.questionIds(ba).length > 0;
    if (total === 1 && counts.green === 1) {
      return hasFindings;
    }
    return markers.length > 0 || hasFindings;
  }

  /**
   * Serializable view of this panel for one test — the status tally, the Focus
   * list, the pattern boxes and the open questions — mirroring render() exactly:
   * the same markers, the same `patternIds` and the same `questionIds`, so both
   * stay empty in the export while nutrientPatternsVisible() is closed. Used by
   * the raw-data JSON export. Null when the panel would not render.
   *
   * Status words rather than the panel's colors: an export is read as text, and
   * "Priority" is the pill the practitioner sees where "red" is an implementation
   * detail.
   */
  static exportInsights(ba: BloodworkAnalysis): {
    tally: Array<{ status: string; count: number }>;
    markersGraded: number;
    focus: Array<{ marker: string; status: string; direction?: 'high' | 'low' }>;
    patterns: Array<{
      patternId: number;
      title: string;
      bullets: string[];
      markers: Array<{ label: string; state: 'high' | 'low' }>;
    }>;
    openQuestions: string[];
  } | null {
    if (!ba?.bloodwork || !NutrientInsights.shouldRender(ba)) return null;
    const markers = ba.getMineralPanelMarkers?.() ?? [];
    const { counts, total } = sectionMarkerCounts(markers, Section.NUTRIENTS);
    const tally = STATUS_ORDER.filter((s) => counts[s] > 0).map((s) => ({
      status: STATUS_LABEL[s],
      count: counts[s],
    }));
    const focus = NutrientInsights.focusRows(markers).map((r) => ({
      marker: r.label,
      status: STATUS_LABEL[r.status],
      ...(r.direction ? { direction: (r.direction === 'up' ? 'high' : 'low') as 'high' | 'low' } : {}),
    }));
    const patterns = NutrientInsights.patternIds(ba).map((id) => {
      const p = NUTRIENT_PATTERN_INSIGHTS[id];
      return {
        patternId: id,
        title: p.title,
        // The description renders as a bullet of its own in the box, so it is
        // one here too.
        bullets: [...p.bullets, ...(p.description ? [p.description] : [])],
        markers: p.logic
          .filter((l) => l.state === 'high' || l.state === 'low')
          .map((l) => ({ label: l.label, state: l.state as 'high' | 'low' })),
      };
    });
    const openQuestions = NutrientInsights.questionIds(ba).map(
      (id) => NUTRIENT_OPEN_QUESTIONS[id].description,
    );
    return { tally, markersGraded: total, focus, patterns, openQuestions };
  }

  /*
   * ── Height estimate for the PDF pre-pagination pass ────────────────────────
   *
   * Every constant below was MEASURED off the rendered panel at its real 300px
   * width (headless Chrome over the compiled custom.scss), not chosen to look
   * plausible. The set it replaces under-counted every panel by 34-70% because
   * it billed one line per bullet where most wrap to three or more, never
   * counted the pattern description (which renders as a bullet of its own),
   * never counted the marker arrow row or the "Focus" / "Not measured yet"
   * labels, and had no floor for the 150px min-height every panel pays.
   *
   * Under-counting is the direction that breaks the document: the packer then
   * prints the next section into a page this one has already filled. So the
   * character width below is carried slightly wide on purpose, which makes
   * every line count err long.
   */
  // Card header + .card-body padding + the 5px panel border, top and bottom.
  static PDF_HEADER_HEIGHT = 93;
  // .anemiaStatusContainer.contentHeightContainer's min-height. The panel body
  // otherwise grows and shrinks with its content, so this only binds on a panel
  // with nothing but a status tally to show.
  static PDF_MIN_BODY_HEIGHT = 60;
  // Bootstrap's body line-height (1.5) at the panel's 12px font.
  static PDF_LINE_HEIGHT = 18;
  // questionStyle overrides that to 1.35.
  static PDF_QUESTION_LINE_HEIGHT = 17;
  // Average glyph width at 12px sans-serif. Measured at 6.0; carried at 6.2 so
  // the wrap arithmetic rounds up rather than down.
  static PDF_CHAR_WIDTH = 6.2;
  // Text width inside the card body, inside a pattern box, and inside a bullet
  // (which also gives up its glyph and gap).
  static PDF_BODY_WIDTH = 270;
  static PDF_BOX_WIDTH = 244;
  static PDF_BULLET_WIDTH = 233;
  // The tally is a two-row grid, so even a single pill pays for the row gap.
  static PDF_TALLY_ROW_HEIGHT = 21;
  static PDF_TALLY_GAP = 5;
  static PDF_TALLY_MARGIN_BOTTOM = 2;
  // "Focus" / "Not measured yet": one 18px line plus its 10px marginTop.
  static PDF_SECTION_LABEL_HEIGHT = 28;
  // The status pill heading a focus group, plus the group's 5px marginTop.
  static PDF_FOCUS_GROUP_HEADER_HEIGHT = 26;
  // A focus row measures 16. Carried at 19 because a long marker name wraps to
  // a second line in the ~131px column a two-column list gives it.
  static PDF_FOCUS_ROW_HEIGHT = 19;
  // A pattern box minus its text: marginTop, border, padding, and the ul's own
  // marginTop. The marker row's 4px is added with the row, which is conditional.
  static PDF_PATTERN_BOX_HEIGHT = 33;
  // Per marker in the arrow row: the glyph with its margins, plus the span's
  // marginLeft.
  static PDF_MARKER_SLOT_WIDTH = 19;
  // A question's 5px marginTop.
  static PDF_QUESTION_MARGIN_TOP = 5;

  /** Rendered lines a string wraps to in `width` px at the panel's body font. */
  static pdfLines(text: string, width: number): number {
    return Math.max(
      1,
      Math.ceil((text.length * NutrientInsights.PDF_CHAR_WIDTH) / width),
    );
  }

  /** The status tally. `statuses` is how many statuses actually occur. */
  static tallyHeight(statuses: number): number {
    if (!statuses) return 0;
    // Same rule as tallyRowStyle: one row up to two pills, two rows beyond. The
    // row gap is only paid when there is a second row to separate.
    const rows = statuses > 2 ? 2 : 1;
    return (
      NutrientInsights.PDF_TALLY_ROW_HEIGHT * rows +
      (rows > 1 ? NutrientInsights.PDF_TALLY_GAP : 0) +
      NutrientInsights.PDF_TALLY_MARGIN_BOTTOM
    );
  }

  /** The ▲/▼ row under a pattern, which wraps between whole markers. */
  static markerRowHeight(logic: NutrientLogicMarker[]): number {
    const arrows = logic.filter((l) => l.state === 'high' || l.state === 'low');
    if (!arrows.length) return 0;
    const width = arrows.reduce(
      (w, l) =>
        w +
        NutrientInsights.PDF_MARKER_SLOT_WIDTH +
        l.label.length * NutrientInsights.PDF_CHAR_WIDTH,
      0,
    );
    const lines = Math.max(1, Math.ceil(width / NutrientInsights.PDF_BOX_WIDTH));
    return 4 + lines * NutrientInsights.PDF_LINE_HEIGHT;
  }

  /**
   * One pattern box. The description is counted with the bullets because
   * renderPatterns appends it to the same list — the omission that made this
   * estimate short by four or five lines on every box.
   */
  static patternBoxHeight(pattern?: NutrientPatternInsight): number {
    if (!pattern) return 0;
    const lines = [...pattern.bullets, ...(pattern.description ? [pattern.description] : [])];
    return (
      NutrientInsights.PDF_PATTERN_BOX_HEIGHT +
      NutrientInsights.pdfLines(pattern.title, NutrientInsights.PDF_BOX_WIDTH) *
        NutrientInsights.PDF_LINE_HEIGHT +
      lines.reduce(
        (h, line) =>
          h +
          NutrientInsights.pdfLines(line, NutrientInsights.PDF_BULLET_WIDTH) *
            NutrientInsights.PDF_LINE_HEIGHT,
        0,
      ) +
      NutrientInsights.markerRowHeight(pattern.logic)
    );
  }

  /** One "Not measured yet" sentence. */
  static questionHeight(question?: NutrientOpenQuestion): number {
    if (!question) return 0;
    return (
      NutrientInsights.PDF_QUESTION_MARGIN_TOP +
      NutrientInsights.pdfLines(question.description, NutrientInsights.PDF_BODY_WIDTH) *
        NutrientInsights.PDF_QUESTION_LINE_HEIGHT
    );
  }

  /**
   * Height of the focus block: the TALLER of its two columns, not the row total.
   * Counting every row (correct while the list was one column) now over-estimates
   * by roughly half, which would push the bar graphs onto their own page for no
   * reason. Exported so the arithmetic is testable on its own.
   */
  static focusBlockHeight(rows: FocusRow[]): number {
    const columnHeights = FOCUS_COLUMNS.map((statuses) =>
      statuses.reduce((h, status) => {
        const n = rows.filter((r) => r.status === status).length;
        return n
          ? h +
              NutrientInsights.PDF_FOCUS_GROUP_HEADER_HEIGHT +
              n * NutrientInsights.PDF_FOCUS_ROW_HEIGHT
          : h;
      }, 0),
    );
    return Math.max(0, ...columnHeights);
  }

  /**
   * This panel's rendered height, in the capture pixels every other number
   * in the PDF's page arithmetic uses (see pdfPanelMetrics).
   */
  static estimatedPdfHeight(ba: BloodworkAnalysis): number {
    if (!NutrientInsights.shouldRender(ba)) return 0;
    const markers = ba.getMineralPanelMarkers?.() ?? [];
    const { counts, total } = sectionMarkerCounts(markers, Section.NUTRIENTS);
    let body = total
      ? NutrientInsights.tallyHeight(STATUS_ORDER.filter((s) => counts[s] > 0).length)
      : 0;

    const rows = NutrientInsights.focusRows(markers);
    if (rows.length) {
      body +=
        NutrientInsights.PDF_SECTION_LABEL_HEIGHT +
        NutrientInsights.focusBlockHeight(rows);
    }

    body += NutrientInsights.patternIds(ba).reduce(
      (sum, id) => sum + NutrientInsights.patternBoxHeight(NUTRIENT_PATTERN_INSIGHTS[id]),
      0,
    );

    const questions = NutrientInsights.questionIds(ba);
    if (questions.length) {
      body +=
        NutrientInsights.PDF_SECTION_LABEL_HEIGHT +
        questions.reduce(
          (sum, id) => sum + NutrientInsights.questionHeight(NUTRIENT_OPEN_QUESTIONS[id]),
          0,
        );
    }

    return (
      NutrientInsights.PDF_HEADER_HEIGHT +
      Math.max(body, NutrientInsights.PDF_MIN_BODY_HEIGHT)
    );
  }


  // ── Rendering ───────────────────────────────────────────────────────────────
  renderArrow = (direction: 'up' | 'down') => (
    <img
      style={arrowStyle}
      src={direction === 'up' ? arrow_up : arrow_down}
      alt={direction === 'up' ? 'high' : 'low'}
    />
  );

  renderTally = (markers: PanelMarker[]) => {
    const { counts, total } = sectionMarkerCounts(markers, Section.NUTRIENTS);
    if (!total) return null;
    const present = STATUS_ORDER.filter((s) => counts[s] > 0);
    return (
      <div style={tallyRowStyle(present.length)}>
        {present.map((s) => (
          <span key={s} style={pillStyle(s)}>
            {counts[s]} {STATUS_LABEL[s]}
          </span>
        ))}
      </div>
    );
  };

  /*
   * Grouped under one pill per status, rather than a pill or a dot on every row.
   *
   * Eleven repeated indicators is the problem either of those solves badly: as
   * dots, the soft Support orange and Monitor yellow are nearly indistinguishable
   * at 8px; as pills, the same uppercase word repeats down the column and buries
   * the marker names, which are the actual content. One pill per group states the
   * status unambiguously and says it once. Rows are already sorted worst-first,
   * so the grouping falls out of the existing order.
   */
  renderFocus = (markers: PanelMarker[]) => {
    const rows = NutrientInsights.focusRows(markers);
    if (!rows.length) return null;
    /*
     * Drop a column that would come back empty (e.g. nothing but Monitor
     * markers), so the list takes the full width instead of rendering a blank
     * half and a squeezed half.
     */
    const columns = FOCUS_COLUMNS.filter((statuses) =>
      rows.some((r) => statuses.includes(r.status)),
    );
    return (
      <>
        <div style={sectionLabelStyle}>Focus</div>
        <div style={columns.length > 1 ? focusColumnsStyle : undefined}>
          {columns.map((statuses, col) => (
            <div key={col}>
              {statuses.map((status) => {
                const group = rows.filter((r) => r.status === status);
                if (!group.length) return null;
                return (
                  <div key={status} style={focusGroupStyle}>
                    <span style={pillStyle(status)}>{STATUS_LABEL[status]}</span>
                    {group.map((r, i) => (
                      <div key={i} style={focusRowStyle}>
                        {/* Arrow LEFT of the name in a fixed-width slot, so names
                            start at the same x whether or not a row has one. */}
                        <span style={arrowSlotStyle}>
                          {r.direction && this.renderArrow(r.direction)}
                        </span>
                        <span>{r.label}</span>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </>
    );
  };

  /** The sheet's Logic cell, as the ▲/▼ row. States without an arrow are skipped. */
  renderMarkerRow = (logic: NutrientLogicMarker[]) => {
    const arrows = logic.filter((l) => l.state === 'high' || l.state === 'low');
    if (!arrows.length) return null;
    // Right-aligned to match the marker rows in the glucose and immune panels.
    return (
      <div style={markerRowStyle}>
        {arrows.map((l, i) => (
          <span key={i} style={{ whiteSpace: 'nowrap', marginLeft: '6px', color: 'black' }}>
            {this.renderArrow(l.state === 'high' ? 'up' : 'down')}
            {l.label}
          </span>
        ))}
      </div>
    );
  };

  renderPatterns = (ids: number[]) => {
    if (!ids.length) return null;
    return (
      <>
        {ids.map((id) => {
          const p = NUTRIENT_PATTERN_INSIGHTS[id];
          return (
            <div key={id} style={boxStyle}>
              <div style={{ fontWeight: 600, color: 'black' }}>{p.title}</div>
              {/* The description is a bullet too. It used to render as a bare
                  paragraph under the bulleted "Consistent with" lines, which made
                  the box look like it had lost a bullet rather than like it was
                  saying two different kinds of thing. */}
              <ul style={bulletListStyle}>
                {[...p.bullets, ...(p.description ? [p.description] : [])].map((line, i) => (
                  <li key={i} style={bulletItemStyle}>
                    <span style={bulletGlyphStyle}>•</span>
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
              {this.renderMarkerRow(p.logic)}
            </div>
          );
        })}
      </>
    );
  };

  /*
   * Open Questions are CLIENT-FACING by decision (2026-08-21): they name the
   * follow-up a practitioner would order, and the client seeing them is what
   * supports that conversation. So they go in the PDF like everything else here.
   * The practitioner-only half (which specific assay, why) stays in the sheet's
   * Practitioner Insights column and is not rendered.
   */
  renderQuestions = (ids: number[]) => {
    if (!ids.length) return null;
    return (
      <>
        <div style={sectionLabelStyle}>Not measured yet</div>
        {ids.map((id) => (
          <div key={id} style={questionStyle}>
            {NUTRIENT_OPEN_QUESTIONS[id].description}
          </div>
        ))}
      </>
    );
  };

  render() {
    const { bloodworkAnalysis: ba } = this.props;
    if (!NutrientInsights.shouldRender(ba)) return null;
    const markers = ba.getMineralPanelMarkers?.() ?? [];
    const patternIds = NutrientInsights.patternIds(ba);
    const questionIds = NutrientInsights.questionIds(ba);

    return (
      <Card className="evaluationPanel" id="nutrient-insights">
        <Card.Header className="evaluationPanelHeading">{PANEL_TITLE}</Card.Header>
        <Card.Body>
          {/* contentHeightContainer: this panel is a list, not a gauge, so it
              sizes to what it has to say. A single-marker Focus list under the
              shared 150px floor printed ~55px of white below the last marker,
              on screen and in the PDF alike. */}
          <div className="anemiaStatusContainer contentHeightContainer">
            {this.renderTally(markers)}
            {this.renderFocus(markers)}
            {this.renderPatterns(patternIds)}
            {this.renderQuestions(questionIds)}
          </div>
        </Card.Body>
      </Card>
    );
  }
}
